import { HeartBank } from "../../../managers/HeartBank.ts";
import template from "./hb-leaderboard.mjs";
import { render } from "lit-html";
import { Kiitti } from "../../../models/Kiitti.ts";

import { Modal } from "flowbite";
import type { ModalOptions, ModalInterface } from "flowbite";
import type { InstanceOptions } from "flowbite";
import {
    getRandomAvatarUrl,
    ringColors,
    getCircle,
    getKiittiSummary,
    getKiitosSummary,
    getName,
    getAuraColor,
    getKiitosAuraColor,
    getKiittiAuraColor,
    getKiitosAuraSize,
    getKiittiAuraSize,
    getAuraRing,
    getAuraName,
    getRandomNumber,
    getPosition,
    haversineMiles
} from "../../global.ts";

export class HbLeaderboard extends HTMLElement {
    private heartbank: HeartBank = new HeartBank();
    private aquarius = new Kiitti();
    private modal: ModalInterface;

    private leaderboard: HTMLElement;
    private tbody: HTMLTableSectionElement;
    private tfoot: HTMLTableRowElement;

    private rank: HTMLButtonElement;
    private kiitos: HTMLButtonElement;
    private kiitti: HTMLButtonElement;

    private account: HTMLTableElement;
    private fund: HTMLTableElement;

    private netKiitos = 0;
    private netKiitti = 0;
    private accountTotal = 0;
    private fundTotal = 0;

    constructor() {
        super();
        render(template(null), this);

        this.leaderboard = this.querySelector("#hb-leaderboard")!;
        this.tbody = this.querySelector("#hb-leaderboard-body")!;
        this.tfoot = this.querySelector("#hb-leaderboard-foot")!;

        this.rank = this.querySelector("#hb-leaderboard-rank")!;
        this.kiitos = this.querySelector("#hb-leaderboard-kiitos")!;
        this.kiitti = this.querySelector("#hb-leaderboard-kiitti")!;

        this.account = this.querySelector("#hb-leaderboard-account")!;
        this.fund = this.querySelector("#hb-leaderboard-fund")!;

        const modalOptions: ModalOptions = {
            placement: "center",
            backdrop: "dynamic",
            backdropClasses:
                "bg-gray-900/50 dark:bg-gray-900/80 fixed inset-0 z-40",
            closable: true,
            onHide: () => {
                // window.location.hash =
                //     "#/onboard/training-" + (this.heartbank.user ? "2" : "1");
            },
            onShow: () => {
                //console.log("modal is shown");
            },
            onToggle: () => {
                //console.log("modal has been toggled");
            }
        };

        // instance options object
        const instanceOptions: InstanceOptions = {
            id: "hb-leaderboard",
            override: true
        };

        this.modal = new Modal(this.leaderboard, modalOptions, instanceOptions);
    }

    connectedCallback() {
        this.rank.addEventListener("click", () => this.renderBody("rank"));
        this.kiitos.addEventListener("click", () => this.renderBody("kiitos"));
        this.kiitti.addEventListener("click", () => this.renderBody("kiitti"));

        this.heartbank.addAuthStateListener((user) => {
            this.account.classList.toggle("hidden", !this.heartbank.isAdmin());
            this.fund.classList.toggle("hidden", !this.heartbank.isAdmin());
        });
    }

    show() {
        if (this.tbody.childElementCount === 0) {
            this.renderBody("rank");
            this.modal.show();
        } else window.location.reload();
    }

    hide() {
        this.modal.hide();
    }

    private renderBody(orderBy: "rank" | "kiitos" | "kiitti") {
        this.netKiitos = 0;
        this.netKiitti = 0;
        this.accountTotal = 0;
        this.fundTotal = 0;
        this.tbody.innerHTML = "";

        const maxMiles = 10;
        const { lat, lng } = getPosition();

        let heroes = JSON.parse(localStorage.getItem("heroes") || "[]"); // need to wait to load all heroes

        if (orderBy === "kiitos") {
            heroes = [...heroes].sort(
                (a, b) => Math.abs(b.kiitos) - Math.abs(a.kiitos)
            );
        } else if (orderBy === "kiitti") {
            heroes = [...heroes].sort(
                (a, b) => Math.abs(b.kiitti) - Math.abs(a.kiitti)
            );
        } else {
            heroes = [...heroes].sort((a, b) => a.rank - b.rank);
        }

        for (const [rank, hero] of heroes.entries()) {
            this.netKiitos += hero.kiitos;
            this.netKiitti += hero.kiitti;
            this.accountTotal += hero.account || 0;
            this.fundTotal += hero.fund || 0;

            const distance = haversineMiles(
                lat,
                lng,
                hero.latitude,
                hero.longitude
            );
            const isNearby = distance <= maxMiles;

            const [kiitosSign, kiitos] = hero.kiitos.numberTuple() || [
                "",
                "Zero"
            ];
            const [kiittiSign, kiitti] = hero.kiitti.numberTuple() || [
                "",
                "Zero"
            ];

            // random ring color: ring-6 ${ringColors[Math.floor(Math.random() * ringColors.length)]}
            const tr = document.createElement("tr");
            tr.className =
                "bg-white border-b dark:bg-gray-800 dark:border-gray-900 border-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600";
            tr.innerHTML =
                `
            <th scope="col" class="px-6 py-3 whitespace-nowrap font-black text-2xl">${hero.rank}</th>
            <th
                scope="row"
                class="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">
                <div class="flex items-center">
                    <img style="border-width:${getKiitosAuraSize(hero.kiitosAmplitude, "s")};border-color:${getKiitosAuraColor(hero.kiitosFrequency)};box-shadow:0 0 0 ${getKiittiAuraSize(hero.kiittiAmplitude, "s")} ${getKiittiAuraColor(hero.kiittiFrequency)};background-color:${getAuraColor(hero.kiitosFrequency, hero.kiittiFrequency)}"
                        class="mr-2 w-10 h-10 rounded-full"
                        src="${hero.photoURL || getRandomAvatarUrl()}"
                        alt="${hero.displayName || "No Name"}" />
                    <div class="ps-3">
                        <div
                            class="text-base font-semibold">
                            ${hero.displayName || "No Name"}
                        </div>
                        <div
                            class="font-normal text-gray-500">
                            @${hero.username}
                        </div>
                    </div>
                </div>
            </th>
            <td
                class="px-6 py-4 bg-gray-50 dark:bg-gray-700 font-bold ${hero.kiitos === 0 ? "text-black dark:text-white" : hero.kiitos > 0 ? "text-purple-600 dark:text-purple-500" : "text-orange-700 dark:text-orange-600"}">
                ${kiitosSign + kiitos}
            </td>
            <td
                class="px-6 py-4 bg-gray-50 dark:bg-gray-700 font-bold ${hero.kiitti === 0 ? "text-black dark:text-white" : hero.kiitti > 0 ? "text-purple-600 dark:text-purple-500" : "text-orange-700 dark:text-orange-600"}">
                ${kiittiSign + kiitti}
            </td>
            <td class="px-6 py-4">
                <div class="flex items-center">
                    <div
                        class="h-2.5 w-2.5 rounded-full ${hero.isBroadcast ? "bg-green-500" : "bg-red-500"} me-2"></div>
                    ${hero.isBroadcast ? (isNearby ? "Nearby" : "Far Away") : "Offline"}
                </div>
            </td>
            ` +
                (this.heartbank.isAdmin()
                    ? `<td class="px-6 py-4 bg-gray-50 dark:bg-gray-700 font-bold text-black dark:text-white">
                            ${hero.account?.toCashAmount3() || "$0.00"}
                        </td>
                        <td class="px-6 py-4 bg-gray-50 dark:bg-gray-700 font-bold text-black dark:text-white">
                            ${hero.fund?.toCashAmount3() || "$0.00"}
                        </td>`
                    : "");

            tr.onclick = async () => {
                const name = getName(hero);
                const [kiitosSign, kiitosKarat] = hero.kiitos.numberTuple();
                const [kiittiSign, kiittiKarat] = hero.kiitti.numberTuple();

                if (
                    confirm(
                        `${getAuraRing(hero.kiitosFrequency || 0, true)} ${name}'s current Kiitos Aura is ${getAuraName(hero.kiitosFrequency)}\n\n${getCircle(hero.kiitos, true)} ${name}'s current Kiitos Balance is ${kiitosSign + kiitosKarat} ${getKiitosSummary(
                            hero.kiitos,
                            name
                        )}\n\n${getAuraRing(hero.kiittiFrequency || 0, false)} ${name}'s current Kiitti Aura is ${getAuraName(hero.kiittiFrequency)}\n\n${getCircle(hero.kiitti, false)} ${name}'s current Kiitti Balance is ${kiittiSign + kiittiKarat} ${getKiittiSummary(
                            hero.kiitti,
                            name
                        )}\n\nWould you like to watch ${name}'s B-Shorts?`
                    )
                )
                    window.open("https://id.333.eco", "_blank");
            };

            this.tbody.appendChild(tr);
        }

        this.renderFoot();
    }

    private async renderFoot() {
        const aquarius = await this.aquarius.getKiitti();
        const netKiitti = this.netKiitti + aquarius;
        const [sign, kiitti] = aquarius.numberTuple();

        const net = this.heartbank.isAdmin()
            ? `<span class="${this.netKiitos === 0 ? "text-green-600" : "text-red-600"}">${this.netKiitos}</span>
            •
            <span class="${netKiitti === 0 ? "text-green-600" : "text-red-600"}">${netKiitti}</span>`
            : "";

        // TODO: later aura color and size for miss aqua
        this.tfoot.innerHTML =
            `
        <th colspan="2"
            scope="row"
            class="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">
            <div class="flex items-center">
                <img
                    class="mr-2 w-10 h-10 rounded-full ring-6 ${ringColors[Math.floor(Math.random() * ringColors.length)]}"
                    src="https://raw.githubusercontent.com/333eco/stock.333.eco/main/miss.aquarius.jpg"
                    alt="Miss Aquarius™" />
                <div class="ps-3">
                    <div
                        class="text-base font-semibold">
                        Miss Aquarius™
                    </div>
                    <div
                        class="font-normal text-gray-500">
                        #Aquarius
                    </div>
                </div>
            </div>
        </th>
        <td class="px-6 py-3">${net}</td>
        <td class="px-6 py-3 font-black ${aquarius === 0 ? "text-black dark:text-white" : aquarius > 0 ? "text-purple-600  dark:text-purple-500" : "text-orange-700 dark:text-orange-600"}">${sign + kiitti}</td>
        <td class="px-6 py-3">🌏<span class="ml-2 text-gray-500 dark:text-gray-400">Earth</span></td>
        ` +
            (this.heartbank.isAdmin()
                ? `<td class="px-6 py-3 text-black dark:text-white">${this.accountTotal.toCashAmount3()}</td>
                    <td class="px-6 py-3 text-black dark:text-white">${this.fundTotal.toCashAmount3()}</td>`
                : "");

        this.tfoot.onclick = async () => {
            const kiitti = await this.aquarius.getKiitti();
            const [sign, karat] = kiitti.numberTuple();
            const frequency = getRandomNumber(0, 100);
            if (
                confirm(
                    `${getAuraRing(frequency)} Miss Aquarius™'s current Aura is ${getAuraName(frequency)}\n\n${getCircle(kiitti)} Miss Aquarius™'s current Kiitti Balance is ${sign + karat} ${getKiittiSummary(
                        kiitti,
                        "Miss Aquarius™"
                    )}\n\nWould you like to watch Miss Aquarius™'s B-Shorts?`
                )
            )
                window.open("https://id.333.eco", "_blank");
        };
    }
}
